<script setup>
import { BIconCheck } from "bootstrap-icons-vue";
import { ref } from "vue";
import IconChevronDown from "@/components/icons/IconChevronDown.vue";
import IconCheckbox from "@/components/icons/IconCheckbox.vue";

const emit = defineEmits(["update:modelValue", "select-all-on-page"]);
const props = defineProps({
  modelValue: {
    type: [String, Number],
    required: true,
  },
  options: {
    type: Array,
    default: () => [
      {
        text: "All",
        value: 10,
      },
      {
        text: "Unread only",
        value: 20,
      },
      {
        text: "Read only",
        value: 100,
      },
    ],
  },
  selectedCount: {
    type: Number,
    default: 0,
  },
  perPage: {
    type: Number,
    default: 0,
  },
  totalItems: {
    type: [String, Number],
    required: true,
  },
  showCheckbox: {
    type: Boolean,
    default: false,
  },
});

const selectedOption = ref(null);

const allItemsChecked = computed(() => (
    props.selectedCount === props.perPage ||
    props.selectedCount === props.totalItems
  )
);

const itemsSelectedPartial = computed(() => {
  return props.selectedCount >= 1 && !allItemsChecked.value;
});

const updatedValue = computed(() => {
  return {
    text: props.selectedCount,
    value: props.selectedCount,
  };
});

const selectOption = (option) => {
  emit("update:modelValue", option);
};

watch(
  () => props.modelValue,
  (val) => {
    selectedOption.value = val;
  },
  {
    immediate: true,
  }
);

const currentSelectedOptionDisplay = computed(() => {
  const option = props.options.find(
    ({ value }) => value === selectedOption.value
  );

  if (option) {
    return option.text;
  }

  return selectedOption.value;
});
</script>

<template>
  <div class="d-flex align-items-center column-gap-1">
    <template v-if="showCheckbox">
      <div class="d-flex cursor-pointer">
        <IconCheckbox
          :width="16"
          :height="16"
          v-if="selectedCount === 0"
          @click="$emit('select-all-on-page')"
        />
        <span
          v-if="allItemsChecked"
          class="position-relative d-flex align-items-center justify-content-center"
          @click="$emit('select-all-on-page')"
        >
          <IconCheckboxChecked :width="16" :height="16" />
          <IconCheckMark :width="10" :height="10" class="position-absolute" />
        </span>
        <span
          v-if="itemsSelectedPartial"
          class="position-relative d-flex align-items-center justify-content-center"
          @click="$emit('select-all-on-page')"
        >
          <IconCheckboxPartial />
          <IconDashPartial class="position-absolute" />
        </span>
      </div>
    </template>
    <dropdown
      no-caret
      header="View"
      label=""
      class="custom-items__dropdown"
      v-if="showCheckbox"
      v-model="selectedOption"
      :current-selected-option="currentSelectedOptionDisplay"
      :options="options"
      :show-search="false"
      @update:modelValue="
        (event) => selectOption(event?.value ? event?.value : event)
      "
    >
    </dropdown>
    <span
      :class="{ 'ms-2': showCheckbox }"
      class="showing-items text-neutral-300 font-size-sm"
    >
      <template v-if="selectedCount">
        <span v-if="selectedCount === perPage || selectedCount === totalItems">
          <span>
            All {{ selectedCount }} of {{ totalItems }} selected in this page
          </span>
          <!--            @TODO-->
          <!-- <span
            v-if="selectedCount !== totalItems"
            class="select-all-items cursor-pointer text-neutral-300 font-size-sm fw-semibold"
            style="text-decoration: underline"
          >
            Select all {{ totalItems }} items
          </span> -->
        </span>
        <span v-else>
          {{ selectedCount }} of {{ totalItems }} items selected
        </span>
      </template>
      <template v-else>
        <template v-if="totalItems > modelValue">
          Showing {{ modelValue }} of {{ totalItems }} items
        </template>
        <template v-else> Showing {{ totalItems }} items </template>
      </template>
    </span>
  </div>
</template>

<style lang="scss" scoped>
.icon-marker {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 4px;
}

.select-all-items {
  margin-left: 0.75rem;
}

:deep(.custom-items__dropdown.dropdown) {
  .dropdown-toggle {
    background: none;
    border: none;
    padding: 0;
    display: flex;
    align-items: center;
  }

  .dropdown-menu {
    margin-top: 5px;
    width: 180px !important;
    left: -25px !important;
  }
}
</style>
